import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { SecureRoute } from 'routes/route-with-permission-check';
import { ROUTE_PATHS } from 'routes/routes-paths';
import { retryImportInsideRoutesDefinition } from 'utils/lazyWithRetry';
const ReportsRoutesLayout = ({ allowOnlyIfFinancialsEnabled, children })=>{
    const [isFinancialsEnabled] = useAccountFeature(AccountFeature.Financials);
    if (allowOnlyIfFinancialsEnabled && !isFinancialsEnabled) {
        return null;
    }
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    });
};
export const reportsRoutes = {
    path: ROUTE_PATHS.REPORTS,
    async lazy () {
        const { ReportsRoute } = await retryImportInsideRoutesDefinition(()=>import('./reports'));
        return {
            element: /*#__PURE__*/ _jsx(SecureRoute, {
                rule: "REPORT:READ",
                children: /*#__PURE__*/ _jsx(ReportsRoute, {})
            })
        };
    },
    children: [
        {
            index: true,
            async lazy () {
                const { ReportsListPage } = await retryImportInsideRoutesDefinition(()=>import('./list'));
                return {
                    element: /*#__PURE__*/ _jsx(SecureRoute, {
                        rule: "REPORT:READ",
                        children: /*#__PURE__*/ _jsx(ReportsListPage, {})
                    })
                };
            }
        },
        {
            path: ROUTE_PATHS.CSAT_REPORT,
            async lazy () {
                const { CsatPage } = await retryImportInsideRoutesDefinition(()=>import('./csat'));
                return {
                    element: /*#__PURE__*/ _jsx(SecureRoute, {
                        rule: "CSAT_REPORT:READ",
                        children: /*#__PURE__*/ _jsx(CsatPage, {})
                    })
                };
            }
        },
        {
            path: `${ROUTE_PATHS.TIMESHEET_REPORT}/${ROUTE_PATHS.MATCH_ALL}`,
            async lazy () {
                const { TimesheetsReportRoute } = await retryImportInsideRoutesDefinition(()=>import('./timesheets'));
                return {
                    element: /*#__PURE__*/ _jsx(SecureRoute, {
                        rule: "TIME_TRACKING_REPORT:READ",
                        children: /*#__PURE__*/ _jsx(TimesheetsReportRoute, {})
                    })
                };
            }
        },
        {
            path: ROUTE_PATHS.PROJECTS_REPORT,
            async lazy () {
                const { ProjectsReportPage } = await retryImportInsideRoutesDefinition(()=>import('components/pages/projects-report'));
                return {
                    element: /*#__PURE__*/ _jsx(SecureRoute, {
                        rule: "PROJECT_PERFORMANCE_REPORT:READ",
                        children: /*#__PURE__*/ _jsx(ProjectsReportPage, {})
                    })
                };
            }
        },
        {
            path: ROUTE_PATHS.SPRINTS_REPORT,
            async lazy () {
                const { SprintsReportPage } = await retryImportInsideRoutesDefinition(()=>import('components/pages/sprints-report/page'));
                return {
                    element: /*#__PURE__*/ _jsx(SecureRoute, {
                        rule: "SPRINT_REPORT:READ",
                        children: /*#__PURE__*/ _jsx(SprintsReportPage, {})
                    })
                };
            }
        },
        {
            path: ROUTE_PATHS.USERS_REPORT,
            async lazy () {
                const { UsersReportPage } = await retryImportInsideRoutesDefinition(()=>import('components/pages/users-report'));
                return {
                    element: /*#__PURE__*/ _jsx(SecureRoute, {
                        rule: "PEOPLE_PERFORMANCE_REPORT:READ",
                        children: /*#__PURE__*/ _jsx(UsersReportPage, {})
                    })
                };
            }
        },
        {
            path: ROUTE_PATHS.TEMPLATE_INSIGHT,
            async lazy () {
                const { TemplateInsight } = await retryImportInsideRoutesDefinition(()=>import('components/pages/template-insight'));
                return {
                    element: /*#__PURE__*/ _jsx(SecureRoute, {
                        rule: "OPERATIONS_INSIGHT_REPORT:READ",
                        children: /*#__PURE__*/ _jsx(TemplateInsight, {})
                    })
                };
            }
        },
        {
            path: `${ROUTE_PATHS.INTERVALS_REPORT}/${ROUTE_PATHS.MATCH_ALL}`,
            async lazy () {
                const { IntervalInsightPage } = await retryImportInsideRoutesDefinition(()=>import('components/pages/intervals-report/page'));
                return {
                    element: /*#__PURE__*/ _jsx(SecureRoute, {
                        rule: "INTERVAL_IQ:READ",
                        children: /*#__PURE__*/ _jsx(IntervalInsightPage, {})
                    })
                };
            }
        },
        {
            path: ROUTE_PATHS.CUSTOM_REPORT,
            async lazy () {
                const { CustomReports } = await retryImportInsideRoutesDefinition(()=>import('components/pages/custom-reports/custom-reports-page'));
                return {
                    element: /*#__PURE__*/ _jsx(SecureRoute, {
                        rule: "CUSTOM_REPORTS:READ",
                        children: /*#__PURE__*/ _jsx(CustomReports, {})
                    })
                };
            }
        },
        {
            path: ROUTE_PATHS.PORTFOLIO_REPORT,
            async lazy () {
                const { ProfitabilityReportPage } = await retryImportInsideRoutesDefinition(()=>import('components/pages/profitability-report'));
                return {
                    element: /*#__PURE__*/ _jsx(ReportsRoutesLayout, {
                        allowOnlyIfFinancialsEnabled: true,
                        children: /*#__PURE__*/ _jsx(SecureRoute, {
                            rule: "PORTFOLIO_REPORT:READ",
                            children: /*#__PURE__*/ _jsx(ProfitabilityReportPage, {})
                        })
                    })
                };
            }
        },
        {
            path: ROUTE_PATHS.UTILIZATION_REPORT,
            async lazy () {
                const { UtilizationReportPage } = await retryImportInsideRoutesDefinition(()=>import('components/pages/utilization-report/page'));
                return {
                    element: /*#__PURE__*/ _jsx(SecureRoute, {
                        rule: "UTILIZATION_REPORT:READ",
                        children: /*#__PURE__*/ _jsx(UtilizationReportPage, {})
                    })
                };
            }
        },
        {
            path: ROUTE_PATHS.TIMESHEET_SUBMISSION_REPORT,
            async lazy () {
                const { TimesheetSubmissionsReportPage } = await retryImportInsideRoutesDefinition(()=>import('components/pages/timesheet-submissions-report/page'));
                return {
                    element: /*#__PURE__*/ _jsx(SecureRoute, {
                        rule: "TIMESHEET_SUBMISSION_REPORT:READ",
                        children: /*#__PURE__*/ _jsx(TimesheetSubmissionsReportPage, {})
                    })
                };
            }
        },
        {
            path: `${ROUTE_PATHS.USER_TIME_ANALYTICS_REPORT}/${ROUTE_PATHS.MATCH_ALL}`,
            async lazy () {
                const { UserTimeAnalyticsReportRoute } = await retryImportInsideRoutesDefinition(()=>import('./time-analytics/users-report'));
                return {
                    element: /*#__PURE__*/ _jsx(SecureRoute, {
                        rule: "PEOPLE_TIME_ANALYTIC_REPORT:READ",
                        children: /*#__PURE__*/ _jsx(UserTimeAnalyticsReportRoute, {})
                    })
                };
            }
        },
        {
            path: `${ROUTE_PATHS.PROJECT_TIME_ANALYTICS_REPORT}/${ROUTE_PATHS.MATCH_ALL}`,
            async lazy () {
                const { ProjectTimeAnalyticsReportRoute } = await retryImportInsideRoutesDefinition(()=>import('./time-analytics/projects-report'));
                return {
                    element: /*#__PURE__*/ _jsx(SecureRoute, {
                        rule: "PROJECT_TIME_ANALYTIC_REPORT:READ",
                        children: /*#__PURE__*/ _jsx(ProjectTimeAnalyticsReportRoute, {})
                    })
                };
            }
        },
        {
            path: `${ROUTE_PATHS.CATEGORY_TIME_ANALYTICS_REPORT}/${ROUTE_PATHS.MATCH_ALL}`,
            async lazy () {
                const { CategoryTimeAnalyticsReportRoute } = await retryImportInsideRoutesDefinition(()=>import('./time-analytics/categories-report'));
                return {
                    element: /*#__PURE__*/ _jsx(SecureRoute, {
                        rule: "CATEGORY_TIME_ANALYTIC_REPORT:READ",
                        children: /*#__PURE__*/ _jsx(CategoryTimeAnalyticsReportRoute, {})
                    })
                };
            }
        }
    ]
};
